import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';
import styled from 'styled-components';
import { useCookies } from 'react-cookie';
// import * as Sentry from '@sentry/browser';

import { useAuth, AuthApiResponse, AuthenticationResultCode } from 'features/auth';
import { Box, Typography, Flex, ButtonIcon, HelperText, Loading, ConditionalLink } from 'components';
import { request, createUrl, useFormikHandles, formatDate, deviceCheck } from 'app/utils';
import usestorage from 'app/utils/storage';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthInput2, AuthButton } from 'features/auth/components';
import { useResources } from 'features/resources';

const ButtonShowPassword = styled(ButtonIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
  color: cyan;
`;

const { REACT_APP_UNSECURE_COOKIE } = process.env;

interface LoginBoxProps {
  showRegisterLink?: boolean;
  description?: string;
  isOffer?: boolean;
}

const LoginBox = ({ showRegisterLink = true, isOffer = false, description }: LoginBoxProps) => {
  const { setAuth, redirectUrl, setRedirectUrl } = useAuth();
  const navigate = useNavigate();
  const { apiGatewayUrl, tenantUuid, schoolUuid, schoolAlias, features } = useSchool();
  const { setAlert, clearAlert } = useAlert();
  const [showPassword, toggleShowPassword] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cookies, setCookie] = useCookies(['token', 'tz']);
  const storage = usestorage('sessionStorage');

  const {
    schools: { schoolXp, schoolIsNasEscolas },
  } = useResources();

  const initialValues: Record<string, any> = {
    email: '',
    password: '',
  };

  const loginWithUsername = schoolXp || features?.hasEducationalInstitution;

  const schoolDVInvest = schoolUuid === '519a0647-e8b4-48bc-9158-d5488d8036bd';

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      email: loginWithUsername
        ? Yup.string().required('Você precisa informar seu login.')
        : Yup.string().email('O e-mail informado está inválido.').required('Você precisa informar seu e-mail.'),
      password: Yup.string().required('Você precisa informar sua senha.'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const { email, password } = values;

      request(
        {
          url: '/api/v1/User/Authentication',
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            login: email,
            password: password,
            tenantUuid,
            ...deviceCheck(),
          },
        },
        true
      )
        .then((response: AxiosResponse<AuthApiResponse>) => {
          setSubmitting(false);

          const { token, fullName, customerUuid, roles, authenticationResult, preferredTimezone } = response.data;
          let goTo = redirectUrl;
          const unsecureCookie = REACT_APP_UNSECURE_COOKIE === 'true' || window.location.hostname === 'localhost';

          setCookie('tz', preferredTimezone, {
            path: '/',
            domain: window.location.hostname,
            expires: formatDate(token.expiresAt).toDate(),
            secure: !unsecureCookie,
          });

          if (authenticationResult === -25) {
            setCookie('token', token.token, {
              path: '/',
              domain: window.location.hostname,
              expires: formatDate(token.expiresAt).toDate(),
              secure: !unsecureCookie,
            });

            navigate('activation');
          } else if (authenticationResult === -26) {
            setCookie('token', token.token, {
              path: '/',
              domain: window.location.hostname,
              expires: formatDate(token.expiresAt).toDate(),
              secure: !unsecureCookie,
            });

            navigate('authentication');
          } else if (authenticationResult !== 0) {
            setAlert({
              type: 'error',
              message: 'Você não tem permissão de acesso.',
            });
          } else {
            // const roleMFA = roles.find((role) => role.name === 'MFACheck');
            const roleIsStudent = roles.find((role) => role.name === 'Student');

            if (roleIsStudent || (!roleIsStudent && isOffer)) {
              const storageRedirectUrl = storage.get('redirect_url');
              if (storageRedirectUrl) {
                goTo = storageRedirectUrl;
                storage.remove('redirect_url');
              }

              // TODO: Isso foi adicionado para identifiar o usuário que veio da LP de coe, para redirecionar ele
              // para a página do curso após o login.

              const enrollSlug = storage.get('enrollSlug');
              if (enrollSlug) {
                const enrollProductPrefix = storage.get('enrollProductPrefix');
                goTo = `/${enrollProductPrefix || 'products'}/${enrollSlug}`;

                storage.remove('enrollSlug');
                storage.remove('enrollProductPrefix');
              }

              setRedirectUrl(null);

              const unsecureCookie = REACT_APP_UNSECURE_COOKIE === 'true' || window.location.hostname === 'localhost';

              setCookie('token', token.token, {
                path: '/',
                domain: window.location.hostname,
                expires: formatDate(token.expiresAt).toDate(),
                secure: !unsecureCookie,
              });

              // Sentry.setUser({ id: studentUuid, email });

              if (window.octadesk) {
                window.octadesk.chat.login({
                  user: {
                    name: fullName,
                    email,
                  },
                  customFields: [
                    {
                      id: 'customerUuid',
                      value: customerUuid,
                    },
                  ],
                });
              }

              setAuth(response.data);
              navigate(goTo || '/');
            }
          }
        })
        .catch((error: AxiosError<AuthApiResponse>) => {
          setSubmitting(false);

          let message = 'Ocorreu um erro na comunicação com serviço de autenticação.<br />Por favor, tente novamente em instantes.';

          if (error?.response?.data?.authenticationResult) {
            switch (error?.response?.data?.authenticationResult) {
              case AuthenticationResultCode.InvalidTenant:
              case AuthenticationResultCode.TenantDisabled:
              case AuthenticationResultCode.SchoolDisabled:
              case AuthenticationResultCode.InvalidSchool:
                message = 'Ocorreu um erro na comunicação com a escola. Entre com contato conosco.';
                break;
              case AuthenticationResultCode.InvalidLogin:
                message = 'Login e/ou senha inválidos';
                break;
              case AuthenticationResultCode.LoginLocked:
                message =
                  "Seu acesso foi bloqueado devido a um número excessivo de tentativas de login inválida. Por favor, crie uma senha nova através do link 'Esqueci a senha', localizado logo abaixo";
                break;
              case AuthenticationResultCode.LoginDisabled:
              case AuthenticationResultCode.CustomerDisabled:
              case AuthenticationResultCode.StudentDisabled:
                message = 'Seu acesso está desativado. Entre com contato conosco.';
                break;
              case AuthenticationResultCode.InvalidPassword:
                message = 'Senha informada é inválida';
                break;
              case AuthenticationResultCode.PasswordExpired:
                message = "Senha expirada. Por favor, crie uma senha nova através do link 'Esqueci a senha', localizado logo abaixo";
                break;
              case AuthenticationResultCode.InvalidRecoveryToken:
                message = 'Token de recuperação inválido';
                break;
              case AuthenticationResultCode.RecoveryTokenExpired:
                message = 'Token de recuperação expirado';
                break;
              case AuthenticationResultCode.InvalidSession:
                message = 'Sessão inválida';
                break;
              case AuthenticationResultCode.TeacherDisabled:
                message = 'Professor desativado';
                break;
              case AuthenticationResultCode.InvalidRefreshToken:
                message = 'Token inválido para atualização';
                break;
              case AuthenticationResultCode.InvalidCustomer:
                message = 'Consumidor inválido';
                break;
              case AuthenticationResultCode.InvalidAccount:
                message = 'Conta inválida';
                break;
              case AuthenticationResultCode.InvalidPasswordPolicyMatch:
              case AuthenticationResultCode.SamePasswordAsOldPassword:
                message = 'Senha inválida conforme política de segurança';
                break;
              case AuthenticationResultCode.RecoveryTokenWithoutAccount:
                message = 'Token de recuperação não possui conta de usuário atrelada';
                break;
              case AuthenticationResultCode.InvalidClientApplication:
                message = 'Client Applicaton inválido';
                break;

              default:
                break;
            }
          }

          setAlert({
            type: 'error',
            message: message,
          });
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur } = useFormikHandles(formik);

  useEffect(() => () => clearAlert(), []); // Clear Alert

  const isMeuPlanoCrypto = schoolUuid === '20b929ad-c773-46e2-93de-991988587835';

  return (
    <>
      {formik.isSubmitting && <Loading alpha />}

      <Alert />

      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column">
          <Flex
            flexDirection="column"
            bg="authBoxBackground"
            borderTopLeftRadius={4}
            borderTopRightRadius={4}
            px={{ m: 30, d: 45 }}
            py={{ m: 30, d: 45 }}
          >
            <Typography as="h1" type="type6" color="authLabelColor" fontWeight="bold">
              {schoolXp ? (
                <>
                  LOGIN PARA
                  <br />
                  DEMAIS USUARIOS
                </>
              ) : (
                'Login'
              )}
            </Typography>

            <Box mt={2}>
              <Typography lineHeight="1.2" type="type2" color="authLabelColor">
                {description || 'Para acessar, preencha abaixo seu e-mail e sua senha.'}
              </Typography>
            </Box>

            {/* email */}
            <Flex flexDirection="column" mt={{ m: 4 }}>
              <label htmlFor="login-email">
                <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                  {loginWithUsername ? 'Login:' : 'E-mail:'}
                </Typography>
              </label>

              <Box position="relative" mt={2}>
                <AuthInput2
                  id="login-email"
                  name="email"
                  type="text"
                  onChange={(event) => {
                    formik.setFieldValue('email', event.currentTarget.value.trim());
                  }}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={formik.values.email}
                  data-haserror={formik.touched.email && formik.errors.email}
                  data-testid="auth-login-email"
                  autoFocus
                  autoComplete="email"
                  tabIndex={1}
                />
              </Box>

              {formik.touched.email && formik.errors.email ? <HelperText type="error">{formik.errors.email}</HelperText> : null}
            </Flex>
            {/* /email */}

            {/* password */}
            <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
              <label htmlFor="login-password">
                <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                  Senha:
                </Typography>
              </label>

              <Box position="relative" mt={2}>
                <ButtonShowPassword
                  aria-label="Mostrar senha digitada"
                  onClick={() => toggleShowPassword((prev) => !prev)}
                  type="button"
                  icon={showPassword ? 'eyeOff' : 'eye'}
                  color="primary_contrast"
                  bg="transparent"
                  iconWidth={22}
                />

                <AuthInput2
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  onChange={(event) => {
                    formik.setFieldValue('password', event.currentTarget.value.trim());
                  }}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={String(formik.values.password).trim()}
                  data-haserror={formik.touched.password && formik.errors.password}
                  style={{ paddingRight: 50 }}
                  data-testid="auth-login-password"
                  autoComplete="current-password"
                  tabIndex={2}
                />
              </Box>

              {formik.touched.password && formik.errors.password ? <HelperText type="error">{formik.errors.password}</HelperText> : null}
            </Flex>
            {/* /password */}

            <Flex justifyContent="flex-end" mt={2}>
              <Link to={createUrl('forgotPassword')} tabIndex={4}>
                <Typography type="type1" color="authLabelColor" colorHover="primary_contrast" upper>
                  {schoolAlias === 'b3' ? 'Esqueci minha senha' : 'Esqueci a senha'}
                </Typography>
              </Link>
            </Flex>
          </Flex>

          <Flex>
            <AuthButton type="submit" data-testid="auth-login-submit" tabIndex={3}>
              Acessar
            </AuthButton>
          </Flex>

          {showRegisterLink ? (
            <Flex bg="authBackground" justifyContent="center" py={3}>
              {schoolIsNasEscolas ? (
                <Link to={createUrl('educational/activate/code')}>
                  <Typography className="btn-auth-external" color="authFloatLinkColor">
                    Ainda não tenho login
                  </Typography>
                </Link>
              ) : (
                <>
                  {schoolDVInvest ? (
                    <>
                      <Link to={createUrl('offers/payment', ['wHztORIRyI'])}>
                        <Typography className="btn-auth-external" color="authFloatLinkColor">
                          Ainda não tenho cadastro
                        </Typography>
                      </Link>
                    </>
                  ) : (
                    <Link to={createUrl('register')}>
                      <Typography className="btn-auth-external" color="authFloatLinkColor">
                        Ainda não tenho cadastro
                      </Typography>
                    </Link>
                  )}
                </>
              )}
            </Flex>
          ) : (
            <>
              {isMeuPlanoCrypto && (
                <Flex bg="authBackground" justifyContent="center" py={3}>
                  <ConditionalLink to="https://meuplanocrypto.com" type="external" target="_blank">
                    <Typography className="btn-auth-external" color="authFloatLinkColor">
                      Ainda não é assinante? Clique aqui
                    </Typography>
                  </ConditionalLink>
                </Flex>
              )}
            </>
          )}
        </Flex>
      </form>
    </>
  );
};

export default LoginBox;
